import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Columns3, CheckSquare, CalendarDays, Mail, MessageSquare, PhoneCall, History, Settings, GraduationCap, LogOut, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark, Avatar } from "@/components/Brand";
import ProfileSetup from "@/components/ProfileSetup";
import { roleLabel } from "@/lib/crm";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  { title: "CRM", items: [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/contacts", label: "Contacts & Leads", icon: Users },
    { to: "/pipeline", label: "Pipeline", icon: Columns3 },
    { to: "/tasks", label: "Tasks & Follow-ups", icon: CheckSquare },
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
  ] },
  { title: "Communications", items: [
    { to: "/campaigns", label: "Email Campaigns", icon: Mail },
    { to: "/sms", label: "SMS Messenger", icon: MessageSquare },
    { to: "/dialer", label: "Power Dialer", icon: PhoneCall },
  ] },
  { title: "Reports", items: [
    { to: "/activity", label: "Activity Log", icon: History },
  ] },
  { title: "Account", items: [
    { to: "/profile", label: "Profile & Settings", icon: Settings },
  ] },
];

function NavItem({ item }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end}
      data-testid={`nav-${item.to.replace(/\//g, "-").replace(/^-/, "") || "dashboard"}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
          isActive ? "bg-[#D4AF37] text-[#19082B] font-semibold" : "text-white/75 hover:bg-white/10 hover:text-white"
        }`}>
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
    </NavLink>
  );
}

function SideNav({ isAdmin }) {
  return (
    <>
      {SECTIONS.map((sec) => (
        <div key={sec.title} className="mb-4">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 font-semibold">{sec.title}</div>
          <nav className="flex flex-col gap-0.5 relative z-10">{sec.items.map((i) => <NavItem key={i.to} item={i} />)}</nav>
        </div>
      ))}
      {isAdmin && (
        <div className="mb-4">
          <div className="px-3 mb-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80 font-semibold">Admin</div>
          <nav className="flex flex-col gap-0.5 relative z-10"><NavItem item={{ to: "/admin/training", label: "Training Panel", icon: GraduationCap }} /></nav>
        </div>
      )}
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#F8F9FC]">
      <aside className="relative grain hidden md:flex w-64 xl:w-72 shrink-0 flex-col bg-[#19082B] px-4 py-6 sticky top-0 h-screen overflow-y-auto" data-testid="sidebar">
        <div className="px-2 mb-8"><BrandMark light /></div>
        <SideNav isAdmin={isAdmin} />
        <button onClick={() => navigate("/profile")} className="mt-auto relative z-10 flex items-center gap-3 rounded-xl border border-[#D4AF37]/25 bg-white/5 p-3 text-left text-white hover:bg-white/10 transition-colors" data-testid="sidebar-profile-btn">
          <Avatar user={user} size={40} testId="sidebar-avatar" />
          <div className="min-w-0">
            <div className="font-semibold truncate text-sm" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-xs text-[#D4AF37]" data-testid="sidebar-user-role">{roleLabel(user.role)}</div>
          </div>
        </button>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b bg-white/95 backdrop-blur-xl px-4 md:px-8 h-16">
          <div className="md:hidden"><BrandMark /></div>
          <nav className="md:hidden flex gap-1 overflow-x-auto">
            {[...SECTIONS.flatMap((s) => s.items), ...(isAdmin ? [{ to: "/admin/training", icon: GraduationCap }] : [])].map((i) => (
              <NavLink key={i.to} to={i.to} end={i.end} className={({ isActive }) => `p-2 rounded ${isActive ? "bg-[#3B185F] text-white" : "text-[#3B185F]"}`}>
                <i.icon className="h-4 w-4" />
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:block text-sm text-stone-500" data-testid="header-date">{today}</div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center rounded-full bg-[#F6F2FB] text-[#3B185F] px-3 py-1 text-xs font-semibold" data-testid="header-role-badge">{roleLabel(user.role)}</span>
            {user.role === "student" && user.days_left <= 7 && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#FFFDF0] text-[#8a6a10] px-3 py-1 text-xs font-semibold" data-testid="access-days-left">
                <Clock className="h-3.5 w-3.5" /> Access ends in {user.days_left} day{user.days_left === 1 ? "" : "s"}
              </span>
            )}
            <button onClick={() => navigate("/profile")} data-testid="header-avatar-btn"><Avatar user={user} size={34} /></button>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn" className="border-[#3B185F]/20">
              <LogOut className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Log out</span>
            </Button>
          </div>
        </header>
        <main className="flex-1 px-4 md:px-8 py-8 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>
      <ProfileSetup />
    </div>
  );
}
